/**
 * Sparebook enhancements. Progressive only: every feature works without this file.
 *  - Scroll reveal for [data-sb-reveal]
 *  - One-tap add for "Complete your ride" rows (hands off to Horizon's cart events)
 *  - Size guide dialogs ([data-sb-dialog-open])
 *  - Variant sync: [data-sb-sync] elements refresh from the HTML Horizon fetches on variant change
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Reveal on scroll ---------------------------------------------- */
function initReveal(root = document) {
  if (!document.documentElement.dataset.sbReveal || reduceMotion.matches || !('IntersectionObserver' in window)) return;

  const targets = root.querySelectorAll('[data-sb-reveal]:not(.is-revealed)');
  if (!targets.length) return;

  document.documentElement.classList.add('sb-reveal-ready');

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  targets.forEach((el) => {
    // Anything already on screen at load is shown immediately (no flash of hidden content).
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.92) {
      el.classList.add('is-revealed');
    } else {
      observer.observe(el);
    }
  });
}

/* ---------- Quick add from cart recommendations --------------------------- */
async function handleQuickAdd(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-sb-quick-add')) return;
  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  button?.setAttribute('aria-busy', 'true');

  try {
    const body = new FormData(form);
    const addResponse = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });
    if (!addResponse.ok) throw new Error('add failed');

    const cart = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    form.dispatchEvent(
      new CartAddEvent(cart, 'sb-cart-recs', {
        source: 'sb-cart-recs',
        itemCount: cart.item_count,
        variantId: String(body.get('id')),
      })
    );
  } catch {
    // Fall back to a normal form post so the item is still added.
    form.removeAttribute('data-sb-quick-add');
    form.submit();
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

/* ---------- Dialogs (size guide) ------------------------------------------ */
function handleDialogClick(event) {
  const opener = event.target instanceof Element ? event.target.closest('[data-sb-dialog-open]') : null;
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-sb-dialog-open'));
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }

  const closer = event.target instanceof Element ? event.target.closest('[data-sb-dialog-close]') : null;
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Click on the backdrop closes the dialog
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('sb-dialog')) {
    const rect = event.target.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) event.target.close();
  }
}

/* ---------- Variant sync -------------------------------------------------- */
function handleVariantUpdate(event) {
  const html = event.detail?.data?.html;
  if (!html || typeof html.querySelectorAll !== 'function') return;
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  const scope = section ?? document;

  scope.querySelectorAll('[data-sb-sync]').forEach((el) => {
    const key = el.getAttribute('data-sb-sync');
    const fresh = html.querySelector(`[data-sb-sync="${CSS.escape(key)}"]`);
    if (!fresh) return;
    if (el.innerHTML !== fresh.innerHTML) el.innerHTML = fresh.innerHTML;
    el.hidden = fresh.hidden;
  });
}

/* ---------- Boot ---------------------------------------------------------- */
document.addEventListener('submit', handleQuickAdd);
document.addEventListener('click', handleDialogClick);
document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);

initReveal();
// Theme editor: re-run when sections are added or re-rendered
document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
